import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";

const CGU = () => {
  const { t, locale } = useLanguage("legal");
  useSEO({ title: t("cgu.title"), canonical: "/cgu", locale });
  const items = (key: string): string[] => {
    const val = t(key, { returnObjects: true });
    return Array.isArray(val) ? val : [];
  };
  return (
    <div className="min-h-screen" dir={locale === "ar" ? "rtl" : "ltr"}>
      <Navbar />
      <main className="container max-w-3xl py-10 space-y-10">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <FileText className="h-5 w-5 text-primary" />
            </div>
            <h1 className="font-display text-3xl font-bold">{t("cgu.title")}</h1>
          </div>
          <p className="text-sm text-muted-foreground">{t("cgu.last_updated")}</p>
          <p className="text-muted-foreground leading-relaxed">{t("cgu.intro")}</p>
        </div>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s1_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("cgu.s1_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s2_title")}</h2>
          <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
            {items("cgu.s2_items").map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s3_title")}</h2>
          <p className="text-muted-foreground">{t("cgu.s3_intro")}</p>
          <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
            {items("cgu.s3_items").map((item, i) => <li key={i}>{item}</li>)}
          </ul>
          <p className="text-sm bg-muted rounded p-3">{t("cgu.s3_note")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s4_title")}</h2>
          <p className="text-muted-foreground">{t("cgu.s4_intro")}</p>
          <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
            {items("cgu.s4_items").map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s5_title")}</h2>
          <p className="text-muted-foreground">{t("cgu.s5_intro")}</p>
          <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
            {items("cgu.s5_items").map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s6_title")}</h2>
          <p className="text-muted-foreground">{t("cgu.s6_intro")}</p>
          <ul className="list-disc list-inside space-y-1.5 text-muted-foreground">
            {items("cgu.s6_items").map((item, i) => <li key={i}>{item}</li>)}
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s7_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("cgu.s7_body1")}</p>
          <p className="text-muted-foreground leading-relaxed">{t("cgu.s7_body2")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s8_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("cgu.s8_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s9_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("cgu.s9_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s10_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("cgu.s10_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s11_title")}</h2>
          <p className="text-muted-foreground leading-relaxed">{t("cgu.s11_body")}</p>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-xl font-semibold">{t("cgu.s12_title")}</h2>
          <p className="text-muted-foreground">{t("cgu.s12_body")}</p>
          <a href="mailto:contact@hirafidz.com" className="text-primary hover:underline">contact@hirafidz.com</a>
        </section>

        <div className="border-t pt-6 flex flex-wrap gap-4 text-sm text-muted-foreground">
          <Link to="/politique-confidentialite" className="hover:text-primary hover:underline">{t("cgu.link_confidentialite")}</Link>
          <Link to="/mentions-legales" className="hover:text-primary hover:underline">{t("cgu.link_mentions")}</Link>
          <Link to="/a-propos" className="hover:text-primary hover:underline">{t("cgu.link_apropos")}</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CGU;